import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { setCurrentRoundActionCreator, selectCurrentActivityType } from '~/bootcamp/slices/bootcamp-activity.slice'

import { selectVccPageLevelsData, selectVccPageTitle } from '~/bootcamp/slices/vcc-data.slice'
import Button from '~/components/Buttons/Button'
import { useSetActivityRoundMutation } from '~/redux/apis/user-stats-api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'

import { BootcampPageUrlParamsType } from '~/bootcamp/types/bootcamp.types'

function RetakeButton({ mode = 'white' }: { mode?: 'white' | 'color' }): React.ReactElement {
  const { t } = useTranslation('bootcamp')
  const { query, asPath } = useRouter()
  const colorInfo = {
    bgClass: 'bg-purple',
    checkedBgClass: 'checked:bg-purple',
    textClass: 'text-purple',
    hex: '#7D38D6',
    borderClass: 'border-purple',
    outlineClass: 'outline-purple',
    ringClass: 'ring-purple',
  }
  const vccLevelsPageData = useAppSelector(selectVccPageLevelsData)
  const activitySetNumber = useAppSelector(selectCurrentActivityType)
  const pageTitle = useAppSelector(selectVccPageTitle)
  const dispatch = useAppDispatch()
  const [isSavingRound, setIsSavingRound] = useState(false)

  const [retakeActivity] = useSetActivityRoundMutation()

  const retakeActivityEmitter = function (): void {
    const { level } = query as unknown as BootcampPageUrlParamsType
    const currentPageData = vccLevelsPageData?.find((el) => el.level === level)
    statisticsAnalytics.logRetakeButtonClick({
      name: currentPageData?.shortTitle || pageTitle,
      setNumber: activitySetNumber,
    })
  }

  const onRetakePress = async (): Promise<void | undefined> => {
    try {
      if (isSavingRound) return
      setIsSavingRound(true)
      const asPathWithoutQuery = asPath.split('?')[0]

      const data = await retakeActivity({
        activityPath: asPathWithoutQuery,
        start: true,
        product: 'bootcamp',
      }).unwrap()

      if (data && data?.roundId) {
        dispatch(setCurrentRoundActionCreator(parseInt(data.roundId, 10)))
        setIsSavingRound(false)
      }
      retakeActivityEmitter()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }

  return (
    <Button
      className={`!w-full border-2 hover:scale-110 duration-300 ${
        mode === 'white' ? colorInfo.textClass : 'text-white'
      } ${mode === 'white' ? 'bg-white' : colorInfo.bgClass} ${colorInfo.borderClass}`}
      onClick={onRetakePress}
      size="medium"
    >
      <span>{t('retake')}</span>
      <svg fill="none" height="25" viewBox="0 0 26 25" width="26" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M18.9684 2.27319L22.0088 5.31365L18.9684 8.3541M6.80657 22.5429L3.76611 19.5024L6.80657 16.462"
          stroke={mode === 'color' ? 'white' : colorInfo.hex}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.02697"
        />
        <path
          d="M22.0088 5.31665H10.8605C8.97895 5.31665 7.17447 6.06409 5.84401 7.39455C4.51356 8.725 3.76611 10.5295 3.76611 12.411M3.76611 19.5054H14.9144C16.796 19.5054 18.6005 18.758 19.9309 17.4275C21.2614 16.0971 22.0088 14.2926 22.0088 12.411"
          stroke={mode === 'color' ? 'white' : colorInfo.hex}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.02697"
        />
      </svg>
    </Button>
  )
}

export default RetakeButton
